import { API, graphqlOperation, Storage } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import awsconfig from '../../../aws-exports';

import { getCurrencyIdTransactionTable, getCurrencyTable } from '../../../graphql/queries';
import { updateCurrencyCurrencyTable, createCurrencyCurrencyTable } from '../../../graphql/mutations';
import {
    GetCurrencyTableResult,
    CurrencyTableItem,
    GetTransactionTableResult,
    GetTransactionTableItem,
} from '../../../types/componentTypes/CurrencyTypes';
import { getErrorMessageFromError, removeFalsyValuesFromCurrency } from '../helpers/currency-helpers';
import { ICreatePromotionState } from '../../../types/componentTypes/assignPromo';
import { getAllowedMarkets } from '../../../utils/rolesUtils';
import { countryList } from '../../../constants/lists';
import { handleAuthError} from '../../../utils/authError';

Storage.configure({
    customPrefix: {
        public: '',
        protected: '',
        private: '',
    },
});

export const updateCurrency = async (currency: CurrencyTableItem) => {
    return await API.graphql(
        graphqlOperation(updateCurrencyCurrencyTable, {
            input: currency,
        })
    );
};

export const createCurrency = async (currency: CurrencyTableItem) =>
    await API.graphql(
        graphqlOperation(createCurrencyCurrencyTable, {
            input: currency,
        })
    );

export const getCurrencies = async (returnOnlyActiveCurrs = false) => {
    const markets = getAllowedMarkets();

    const response = (await API.graphql(graphqlOperation(getCurrencyTable, {
        // If getAllowedMarkets are all markets. Send empty array to skip the filtration at all.
        // TODO: remove when roles and permissions are utilised properly
        markets: markets.length === Object.keys(countryList).length ? [] : markets,
    }))) as GraphQLResult<GetCurrencyTableResult>;

    if (returnOnlyActiveCurrs)
        return (response.data?.getCurrencyTable.items as CurrencyTableItem[]).filter(
            (item) => item.is_active === null || item.is_active
        );

    return (response.data?.getCurrencyTable.items as CurrencyTableItem[]).filter(
        (item) => item.is_active || item.is_active === null
    );
};

export const getAndGroupCurrencies = async (
    currencies?: CurrencyTableItem[]
): Promise<Record<string, string[]>> => {
    const items = currencies ? currencies : await getCurrencies();

    if (!items.length) {
        return {};
    }

    const groupedCurr = items.reduce((acc: { [key: string]: string[] }, item) => {
        const countryName: string = item.country || 'others';
        const currNames: string[] = acc[countryName];

        if (!currNames) {
            return { ...acc, [countryName]: [item.name.toLowerCase()] };
        }

        currNames.push(item.name.toLowerCase());
        return { ...acc, [countryName]: currNames };
    }, {});

    return groupedCurr;
};

export const getTransactionsForCurrencyId = async (currencyId: string) => {
    try {
        const response = (await API.graphql(
            graphqlOperation(getCurrencyIdTransactionTable, {
                currency_id: currencyId,
            })
        )) as GraphQLResult<GetTransactionTableResult>;

        return response.data?.getCurrencyIdTransactionTable.items as GetTransactionTableItem[];
    } catch (error) {
        if (!handleAuthError(error)) {
            console.log(error);
        }
        throw new Error(getErrorMessageFromError(error));
    }
};

export const deleteIconFromS3 = async (fileName: string) => {
    const bucket = awsconfig.aws_user_files_s3_bucket.replace('private', 'public');
    try {
        return await Storage.remove(fileName, { bucket });
    } catch (error) {
        throw new Error((error as Error).message);
    }
};

export const removeIconFromCurrency = async (fileLocation: string, currency?: CurrencyTableItem) => {
    try {
        const urlParts = fileLocation.split('/');
        const fileName = urlParts.pop(); // last one is file name

        const currencyId = currency ? currency.currency_id : urlParts.pop(); // next pop(penultimate) is the currency_id

        const filePath = `icons/${currencyId}/${fileName}`;
        await deleteIconFromS3(filePath);
        if (!currency) return;
        await updateCurrency({
            ...removeFalsyValuesFromCurrency(currency),
            icon_url: '',
        });

        return;
    } catch (error) {
        console.error(error);
        throw new Error((error as Error).message);
    }
};

export const getActiveLinkedPromotionsForCurrency = async (currency: CurrencyTableItem) => {
    if (!currency.currency_assignment) return [];

    const parsedCurrAssignment: Record<string, string[]> = JSON.parse(currency.currency_assignment);

    return Object.values(parsedCurrAssignment)
        .flatMap((el) => el)
        .reduce<Promise<string[] | undefined>>(async (acc, configId) => {
            try {
                const fileName = `${configId}/conf.txt`;
                const response = await Storage.get(fileName, { download: true });

                if (!(response?.Body && response.Body instanceof Blob)) {
                    return acc;
                }

                const { configurationParameters } = (await new Response(response.Body).json()) as ICreatePromotionState;
                if (!configurationParameters) return acc;

                const { configurationStartUtc, configurationEndUtc } = configurationParameters;
                const now = new Date().getTime();

                const isPromotionActive = now > +configurationStartUtc && now < +configurationEndUtc;

                if (isPromotionActive) return [...((await acc) as string[]), configId];

                return acc;
            } catch (error) {
                if (!handleAuthError(error)) {
                    console.log(error);
                }
                throw new Error(getErrorMessageFromError(error));
            }
        }, Promise.resolve([]));
};
